<template>
  <span
    ref="trigger"
    class="gh-prompt-popover"
    @mouseenter="onEnter"
    @mouseleave="scheduleHide"
  >
    <span class="gh-prompt-popover__text">{{ text }}</span>
    <div
      v-if="interactive"
      v-show="visible && hasContent"
      ref="popup"
      class="gh-prompt-popover__popup"
      :class="{
        'gh-prompt-popover__popup--tooltip': tooltip,
        'gh-prompt-popover__popup--above': placement === 'above',
        'gh-prompt-popover__popup--below': placement === 'below',
      }"
      :style="popupStyle"
      role="tooltip"
      @mouseenter="onEnter"
      @mouseleave="scheduleHide"
    >{{ text }}</div>
  </span>
</template>

<script>
const HIDE_DELAY_DEFAULT = 200;
const EDGE = 12;

export default {
  name: 'GenerationHistoryPromptPopover',
  props: {
    text: {
      type: String,
      default: '',
    },
    tooltip: {
      type: Boolean,
      default: false,
    },
    interactive: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      visible: false,
      placement: 'below',
      popupStyle: {},
      hideTimer: null,
      positionListenersBound: false,
    };
  },
  computed: {
    hasContent() {
      return !!(this.text && String(this.text).trim());
    },
    hideDelay() {
      return HIDE_DELAY_DEFAULT;
    },
  },
  beforeDestroy() {
    this.clearHideTimer();
    this.unbindPositionListeners();
  },
  methods: {
    clearHideTimer() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    },
    bindPositionListeners() {
      if (this.positionListenersBound) {
        return;
      }
      window.addEventListener('scroll', this.onReposition, true);
      window.addEventListener('resize', this.onReposition);
      this.positionListenersBound = true;
    },
    unbindPositionListeners() {
      if (!this.positionListenersBound) {
        return;
      }
      window.removeEventListener('scroll', this.onReposition, true);
      window.removeEventListener('resize', this.onReposition);
      this.positionListenersBound = false;
    },
    onReposition() {
      if (!this.visible) {
        return;
      }
      this.updatePopupPosition();
    },
    onEnter() {
      if (!this.interactive) {
        return;
      }
      this.clearHideTimer();
      if (!this.hasContent) {
        return;
      }
      this.visible = true;
      this.bindPositionListeners();
      this.$nextTick(() => {
        this.updatePopupPosition();
        // Remeasure after layout so tipHeight clamp can run.
        this.$nextTick(() => {
          this.updatePopupPosition();
        });
      });
    },
    scheduleHide() {
      if (!this.interactive) {
        return;
      }
      this.clearHideTimer();
      this.hideTimer = setTimeout(() => {
        this.visible = false;
        this.placement = 'below';
        this.hideTimer = null;
        this.unbindPositionListeners();
      }, this.hideDelay);
    },
    updatePopupPosition() {
      const trigger = this.$refs.trigger;
      const popup = this.$refs.popup;
      if (!trigger) {
        return;
      }
      const rect = trigger.getBoundingClientRect();
      const popupWidth = Math.min(this.tooltip ? 360 : 420, window.innerWidth - 24);
      let left = rect.left;
      if (left + popupWidth > window.innerWidth - EDGE) {
        left = window.innerWidth - popupWidth - EDGE;
      }
      if (left < EDGE) {
        left = EDGE;
      }
      const gap = this.tooltip ? 2 : 6;
      // Prefer upper half → below, lower half → above; flip if preferred side overflows.
      const triggerMidY = rect.top + (rect.height / 2);
      let placeBelow = triggerMidY < (window.innerHeight / 2);
      const tipHeight = (popup && popup.offsetHeight) || 0;
      if (tipHeight > 0) {
        const canBelow = (rect.bottom + gap + tipHeight) <= (window.innerHeight - EDGE);
        const canAbove = (rect.top - gap - tipHeight) >= EDGE;
        if (placeBelow && !canBelow && canAbove) {
          placeBelow = false;
        } else if (!placeBelow && !canAbove && canBelow) {
          placeBelow = true;
        } else if (!canBelow && !canAbove) {
          // Neither side fits fully — clamp absolute top inside viewport.
          this.placement = 'below';
          const clampedTop = Math.min(
            Math.max(EDGE, rect.top - gap - tipHeight),
            Math.max(EDGE, window.innerHeight - tipHeight - EDGE),
          );
          this.popupStyle = {
            top: `${clampedTop}px`,
            left: `${left}px`,
            width: `${popupWidth}px`,
            transform: 'none',
          };
          return;
        }
      }
      this.placement = placeBelow ? 'below' : 'above';

      if (placeBelow) {
        this.popupStyle = {
          top: `${rect.bottom + gap}px`,
          left: `${left}px`,
          width: `${popupWidth}px`,
          transform: 'none',
        };
      } else {
        this.popupStyle = {
          top: `${rect.top - gap}px`,
          left: `${left}px`,
          width: `${popupWidth}px`,
          transform: 'translateY(-100%)',
        };
      }
    },
  },
};
</script>
